import { createFileRoute, Link } from '@tanstack/react-router';
import {
  addMonths,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  parseISO,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from 'date-fns';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/services/api';
import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@ludolio/shared-ui';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AlertCircle,
  Building2,
  CalendarDays,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Eye,
  Gamepad2,
  Info,
  RotateCcw,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type {
  DeveloperGameAnalytics,
  DeveloperGameTrendAnalytics,
  DeveloperGameTrendPoint,
  Game,
  GameAnalyticsMetrics,
} from '@/types';

export const Route = createFileRoute('/_authenticated/dashboard')({
  component: DashboardPage,
});

type TrendPeriod = '7d' | '30d' | '90d' | 'all';
type DateRangePreset = TrendPeriod | 'custom';

const PERIOD_LABELS: Record<TrendPeriod, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  'all': 'All time',
};

const METRIC_DEFINITIONS = {
  installs: 'Total installation records for this game within the selected date range.',
  uniquePlayers: 'Distinct user accounts that played this game within the selected date range.',
  totalPlayTime: 'Sum of recorded play session duration for this game within the selected date range.',
  activeOrgs: 'Number of distinct organizations or universities with at least one install or play session in the selected date range. Organization names are not shown.',
  sessionsPlayed: 'Total number of play sessions recorded for this game within the selected date range.',
  avgSessionLength: 'Average play session duration, calculated as total play time divided by sessions played in the selected date range.',
  newPlayers: 'User accounts whose first-ever play session for this game happened within the selected date range.',
  repeatPlayerRate: 'Percentage of players in the selected date range who played more than one session.',
} as const;

function getPeriodDays(period: TrendPeriod) {
  if (period === '7d') return 7;
  if (period === '30d') return 30;
  if (period === '90d') return 90;
  return null; // 'all'
}

function toDateKey(date: Date) {
  return format(date, 'yyyy-MM-dd');
}

const ALL_TIME_START = '2020-01-01';

function buildPresetRange(period: TrendPeriod) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (period === 'all') {
    return { startDate: ALL_TIME_START, endDate: toDateKey(today) };
  }

  const startDate = subDays(today, getPeriodDays(period)! - 1);
  return {
    startDate: toDateKey(startDate),
    endDate: toDateKey(today),
  };
}

function getPresetFromDateRange(startDate: string, endDate: string): DateRangePreset {
  const parsedStart = parseISO(startDate);
  const parsedEnd = parseISO(endDate);
  if (Number.isNaN(parsedStart.getTime()) || Number.isNaN(parsedEnd.getTime())) return 'custom';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const normalizedEnd = new Date(parsedEnd);
  normalizedEnd.setHours(0, 0, 0, 0);
  if (!isSameDay(normalizedEnd, today)) return 'custom';

  const totalDays = differenceInCalendarDays(normalizedEnd, parsedStart) + 1;
  if (totalDays === 7) return '7d';
  if (totalDays === 30) return '30d';
  if (totalDays === 90) return '90d';

  // Check if start matches ALL_TIME_START
  if (startDate === ALL_TIME_START) return 'all';

  return 'custom';
}

function formatRangeValue(startDate: string, endDate: string) {
  return `${format(parseISO(startDate), 'dd/MM/yyyy')} - ${format(parseISO(endDate), 'dd/MM/yyyy')}`;
}

function formatDuration(seconds: number) {
  if (seconds <= 0) {
    return '0m';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  if (minutes > 0) {
    return `${minutes}m`;
  }

  return `${seconds}s`;
}

function MetricLabel({
  label,
  description,
  icon: Icon,
}: {
  label: string;
  description: string;
  icon?: LucideIcon;
}) {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      {Icon ? <Icon className="h-4 w-4" /> : null}
      <span>{label}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center text-muted-foreground transition-colors hover:text-foreground"
            aria-label={`Definition of ${label}`}
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className="max-w-72 text-xs leading-relaxed">
          {description}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

function RangeCalendarMonth({
  month,
  draftStart,
  draftEnd,
  onSelect,
}: {
  month: Date;
  draftStart: Date | null;
  draftEnd: Date | null;
  onSelect: (day: Date) => void;
}) {
  const monthStart = startOfMonth(month);
  const calendarDays = eachDayOfInterval({
    start: startOfWeek(monthStart),
    end: endOfWeek(endOfMonth(month)),
  });

  return (
    <div className="rounded-lg border border-border p-3">
      <div className="mb-3 text-sm font-semibold text-[hsl(var(--color-foreground))]">{format(month, 'MMMM yyyy')}</div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] uppercase text-muted-foreground">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((label) => (
          <div key={label} className="py-1">{label}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((day) => {
          const isStart = !!draftStart && isSameDay(day, draftStart);
          const isEnd = !!draftEnd && isSameDay(day, draftEnd);
          const inRange = !!draftStart && !!draftEnd && isWithinInterval(day, { start: draftStart, end: draftEnd });
          const isOutsideMonth = !isSameMonth(day, monthStart);

          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => onSelect(day)}
              className={cn(
                'h-9 rounded-md text-sm transition-colors',
                isOutsideMonth ? 'text-muted-foreground/35' : 'text-[hsl(var(--color-foreground))] hover:bg-muted',
                inRange && 'bg-[hsl(var(--color-primary))]/12',
                (isStart || isEnd) && 'bg-[hsl(var(--color-primary-action))] text-[hsl(var(--color-primary-action-foreground))] hover:bg-[hsl(var(--color-primary-action-hover))]'
              )}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DateRangePicker({
  startDate,
  endDate,
  description,
  onApply,
}: {
  startDate: string;
  endDate: string;
  description?: string;
  onApply: (nextStartDate: string, nextEndDate: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(startOfMonth(parseISO(startDate)));
  const [draftStart, setDraftStart] = useState<Date | null>(parseISO(startDate));
  const [draftEnd, setDraftEnd] = useState<Date | null>(parseISO(endDate));

  const syncDraft = () => {
    setDraftStart(parseISO(startDate));
    setDraftEnd(parseISO(endDate));
    setVisibleMonth(startOfMonth(parseISO(startDate)));
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) syncDraft();
    setOpen(nextOpen);
  };

  const handleSelectDay = (day: Date) => {
    if (!draftStart || draftEnd) {
      setDraftStart(day);
      setDraftEnd(null);
      return;
    }

    if (isBefore(day, draftStart)) {
      setDraftStart(day);
      setDraftEnd(draftStart);
      return;
    }

    setDraftEnd(day);
  };

  const handleApply = () => {
    if (!draftStart) return;
    const resolvedEnd = draftEnd ?? draftStart;
    const normalizedStart = isAfter(draftStart, resolvedEnd) ? resolvedEnd : draftStart;
    const normalizedEnd = isAfter(draftStart, resolvedEnd) ? draftStart : resolvedEnd;
    onApply(toDateKey(normalizedStart), toDateKey(normalizedEnd));
    setOpen(false);
  };

  const selectedLabel = draftStart
    ? `${format(draftStart, 'dd/MM/yyyy')} - ${format(draftEnd ?? draftStart, 'dd/MM/yyyy')}`
    : 'Select range';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Button type="button" variant="outline" className="min-w-[230px] justify-between text-left font-normal" onClick={() => handleOpenChange(true)}>
        <span className="inline-flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          <span>{formatRangeValue(startDate, endDate)}</span>
        </span>
      </Button>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Choose date range</DialogTitle>
          <DialogDescription>{description ?? 'Select a start and end date for this chart.'}</DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3">
          <Button type="button" variant="ghost" size="icon" onClick={() => setVisibleMonth(subMonths(visibleMonth, 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-sm text-muted-foreground">{selectedLabel}</div>
          <Button type="button" variant="ghost" size="icon" onClick={() => setVisibleMonth(addMonths(visibleMonth, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <RangeCalendarMonth month={visibleMonth} draftStart={draftStart} draftEnd={draftEnd} onSelect={handleSelectDay} />
          <RangeCalendarMonth month={addMonths(visibleMonth, 1)} draftStart={draftStart} draftEnd={draftEnd} onSelect={handleSelectDay} />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleApply} disabled={!draftStart}>
            Apply range
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DateRangePresetSelect({ value, onChange }: { value: DateRangePreset; onChange: (nextValue: DateRangePreset) => void }) {
  return (
    <Select value={value} onValueChange={(nextValue) => onChange(nextValue as DateRangePreset)}>
      <SelectTrigger className="w-[160px]">
        <SelectValue placeholder="Select range" />
      </SelectTrigger>
      <SelectContent>
        {(['7d', '30d', '90d', 'all'] as TrendPeriod[]).map((period) => (
          <SelectItem key={period} value={period}>
            {PERIOD_LABELS[period]}
          </SelectItem>
        ))}
        <SelectItem value="custom">Custom range</SelectItem>
      </SelectContent>
    </Select>
  );
}

function AnalyticsRangeControls({
  startDate,
  endDate,
  preset,
  onApplyRange,
  onChangePreset,
}: {
  startDate: string;
  endDate: string;
  preset: DateRangePreset;
  onApplyRange: (nextStartDate: string, nextEndDate: string) => void;
  onChangePreset: (nextValue: DateRangePreset) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        description="Select a start and end date for the summary metrics and charts."
        onApply={onApplyRange}
      />
      <DateRangePresetSelect value={preset} onChange={onChangePreset} />
    </div>
  );
}

function formatNumber(value: number) {
  return value.toLocaleString();
}

function formatPercentage(value: number) {
  return Number.isInteger(value) ? `${value}` : value.toFixed(1);
}

function getTrendTickStep(pointCount: number) {
  if (pointCount <= 7) return 1;
  if (pointCount <= 14) return 2;
  if (pointCount <= 31) return 4;
  if (pointCount <= 90) return 7;
  return Math.max(1, Math.ceil(pointCount / 12));
}

function buildTrendTicks(trend: { date: string }[]) {
  const step = getTrendTickStep(trend.length);
  return Array.from(
    new Set(
      trend
        .filter((_, index) => index % step === 0 || index === trend.length - 1)
        .map((point) => point.date)
    )
  );
}

function formatTrendDate(date: string) {
  return format(parseISO(date), 'MMM d, yyyy');
}

/** Aggregate daily data points into weekly buckets when there are too many points. */
function aggregateToWeekly(data: DeveloperGameTrendPoint[]): DeveloperGameTrendPoint[] {
  const buckets: DeveloperGameTrendPoint[] = [];
  for (let i = 0; i < data.length; i += 7) {
    const chunk = data.slice(i, i + 7);
    buckets.push({
      date: chunk[chunk.length - 1].date, // use last day so the most recent bucket shows today
      installs: chunk.reduce((s, p) => s + p.installs, 0),
      uniquePlayers: chunk.reduce((s, p) => s + p.uniquePlayers, 0),
      sessionsPlayed: chunk.reduce((s, p) => s + p.sessionsPlayed, 0),
      playTimeSeconds: chunk.reduce((s, p) => s + p.playTimeSeconds, 0),
    });
  }
  return buckets;
}

/** Aggregate daily data points into monthly buckets for very long ranges. */
function aggregateToMonthly(data: DeveloperGameTrendPoint[]): DeveloperGameTrendPoint[] {
  const monthMap = new Map<string, DeveloperGameTrendPoint>();
  for (const p of data) {
    const key = p.date.slice(0, 7); // YYYY-MM
    const existing = monthMap.get(key);
    if (!existing) {
      monthMap.set(key, { ...p });
    } else {
      existing.installs += p.installs;
      existing.uniquePlayers += p.uniquePlayers;
      existing.sessionsPlayed += p.sessionsPlayed;
      existing.playTimeSeconds += p.playTimeSeconds;
    }
  }
  // Use last date in each month so the most recent month shows today
  for (const p of data) {
    const key = p.date.slice(0, 7);
    const bucket = monthMap.get(key);
    if (bucket) bucket.date = p.date;
  }
  return Array.from(monthMap.values());
}

function AnalyticsMetricCard({
  label,
  description,
  value,
  icon,
}: {
  label: string;
  description: string;
  value: string | number;
  icon?: LucideIcon;
}) {
  return (
    <div className="rounded-md bg-muted/40 p-3">
      <MetricLabel label={label} description={description} icon={icon} />
      <div className="mt-2 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function GameGrowthCharts({ trend, startDate, endDate }: { trend: DeveloperGameTrendPoint[]; startDate: string; endDate: string }) {
  // Build chart data: always show a chart even when empty (flat 0 line).
  // For large ranges, trim leading all-zero entries and aggregate into
  // weekly buckets so the chart stays readable.
  const chartData = useMemo(() => {
    let source: DeveloperGameTrendPoint[];

    if (trend.length > 0) {
      // Trim leading all-zero entries so the chart starts at the first real data
      const firstNonZero = trend.findIndex(
        (p) => p.installs > 0 || p.uniquePlayers > 0 || p.sessionsPlayed > 0 || p.playTimeSeconds > 0
      );
      source = firstNonZero > 0 ? trend.slice(firstNonZero) : trend;
    } else {
      // No trend data — generate zero-filled points for the selected range
      source = eachDayOfInterval({ start: parseISO(startDate), end: parseISO(endDate) }).map((d) => ({
        date: format(d, 'yyyy-MM-dd'),
        installs: 0,
        uniquePlayers: 0,
        sessionsPlayed: 0,
        playTimeSeconds: 0,
      }));
    }

    // Aggregate into monthly/weekly buckets when there are too many daily points
    if (source.length > 365) {
      source = aggregateToMonthly(source);
    } else if (source.length > 90) {
      source = aggregateToWeekly(source);
    }

    return source.map((point) => ({
      ...point,
      playTimeMinutes: Number((point.playTimeSeconds / 60).toFixed(1)),
      avgSessionMinutes: point.sessionsPlayed > 0
        ? Number((point.playTimeSeconds / point.sessionsPlayed / 60).toFixed(1))
        : 0,
    }));
  }, [trend, startDate, endDate]);

  const xAxisTicks = useMemo(() => buildTrendTicks(chartData as DeveloperGameTrendPoint[]), [chartData]);
  const gridColor = 'hsl(var(--color-border))';
  const textColor = 'hsl(var(--color-muted-foreground))';
  const tooltipStyle = {
    backgroundColor: 'hsl(var(--color-card))',
    border: '1px solid hsl(var(--color-border))',
    borderRadius: '0.75rem',
    color: 'hsl(var(--color-foreground))',
  };
  const chartMinWidth = Math.max(680, chartData.length * 44);

  return (
    <div className="space-y-4">
      <div className="pt-2">
        <h3 className="mb-4 text-sm font-semibold text-[hsl(var(--color-foreground))]">Play Time &amp; Installations</h3>
        <div className="overflow-x-auto pb-2">
          <div style={{ minWidth: `${chartMinWidth}px`, height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis
                  dataKey="date"
                  ticks={xAxisTicks}
                  interval={0}
                  stroke={textColor}
                  tick={{ fontSize: 11 }}
                  tickFormatter={formatTrendDate}
                />
                <YAxis yAxisId="left" stroke={textColor} tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" stroke={textColor} tick={{ fontSize: 11 }} />
                <RechartsTooltip
                  contentStyle={tooltipStyle}
                  labelFormatter={(value) => formatTrendDate(String(value))}
                  formatter={(value, name) => [formatNumber(Number(value)), name]}
                />
                <Legend wrapperStyle={{ color: textColor, fontSize: 12 }} />
                <Line yAxisId="left" type="monotone" dataKey="installs" name="Installations" stroke="#ff4d4f" strokeWidth={2} dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="playTimeMinutes" name="Play Time (min)" stroke="#f59e0b" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="pt-2">
        <h3 className="mb-4 text-sm font-semibold text-[hsl(var(--color-foreground))]">Active Users</h3>
        <div className="overflow-x-auto pb-2">
          <div style={{ minWidth: `${chartMinWidth}px`, height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis
                  dataKey="date"
                  ticks={xAxisTicks}
                  interval={0}
                  stroke={textColor}
                  tick={{ fontSize: 10 }}
                  tickFormatter={formatTrendDate}
                />
                <YAxis yAxisId="left" stroke={textColor} tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" stroke={textColor} tick={{ fontSize: 11 }} />
                <RechartsTooltip
                  contentStyle={tooltipStyle}
                  labelFormatter={(value) => formatTrendDate(String(value))}
                  formatter={(value, name) => [formatNumber(Number(value)), name]}
                />
                <Legend wrapperStyle={{ color: textColor, fontSize: 12 }} />
                <Area yAxisId="left" type="monotone" dataKey="uniquePlayers" name="Active Users" stroke="#6366f1" fill="#6366f180" />
                <Area yAxisId="right" type="monotone" dataKey="avgSessionMinutes" name="Avg Session (min)" stroke="#f59e0b" fill="#f59e0b60" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function GameAnalyticsTab({
  game,
  analytics,
  trend,
  startDate,
  endDate,
  preset,
  onApplyRange,
  onChangePreset,
}: {
  game: Game;
  analytics: GameAnalyticsMetrics;
  trend: DeveloperGameTrendPoint[];
  startDate: string;
  endDate: string;
  preset: DateRangePreset;
  onApplyRange: (nextStartDate: string, nextEndDate: string) => void;
  onChangePreset: (nextValue: DateRangePreset) => void;
}) {
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          {game.iconUrl ? (
            <img src={game.iconUrl} alt={game.title} className="h-12 w-12 rounded" />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded bg-muted">
              <Gamepad2 className="h-6 w-6 text-muted-foreground" />
            </div>
          )}
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-medium">{game.title}</p>
              <Badge variant={
                game.reviewStatus === 'approved' ? 'success' :
                game.reviewStatus === 'pending_review' ? 'secondary' :
                game.reviewStatus === 'rejected' ? 'destructive' : 'outline'
              }>
                {game.reviewStatus === 'none' ? 'Draft' : game.reviewStatus.replace('_', ' ')}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">App ID: {game.appId}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Link to="/games/$gameId" params={{ gameId: game.id }}>
            <Button variant="ghost" size="sm">
              <Eye className="mr-1 h-4 w-4" />
              View
            </Button>
          </Link>
          <AnalyticsRangeControls
            startDate={startDate}
            endDate={endDate}
            preset={preset}
            onApplyRange={onApplyRange}
            onChangePreset={onChangePreset}
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <AnalyticsMetricCard label="Installs" description={METRIC_DEFINITIONS.installs} value={formatNumber(analytics.installCount)} icon={Download} />
        <AnalyticsMetricCard label="Unique Players" description={METRIC_DEFINITIONS.uniquePlayers} value={formatNumber(analytics.uniquePlayers)} icon={Users} />
        <AnalyticsMetricCard label="Total Play Time" description={METRIC_DEFINITIONS.totalPlayTime} value={formatDuration(analytics.totalPlayTimeSeconds)} icon={Clock} />
        <AnalyticsMetricCard label="Active Orgs / Universities" description={METRIC_DEFINITIONS.activeOrgs} value={formatNumber(analytics.activeOrgCount)} icon={Building2} />
        <AnalyticsMetricCard label="Sessions Played" description={METRIC_DEFINITIONS.sessionsPlayed} value={formatNumber(analytics.sessionsPlayed)} />
        <AnalyticsMetricCard label="Avg Session Length" description={METRIC_DEFINITIONS.avgSessionLength} value={formatDuration(analytics.averageSessionLengthSeconds)} />
        <AnalyticsMetricCard label="New Players" description={METRIC_DEFINITIONS.newPlayers} value={formatNumber(analytics.newPlayers)} />
        <AnalyticsMetricCard label="Repeat Player Rate" description={METRIC_DEFINITIONS.repeatPlayerRate} value={`${formatPercentage(analytics.repeatPlayerRate)}%`} icon={RotateCcw} />
      </div>

      <GameGrowthCharts trend={trend} startDate={startDate} endDate={endDate} />
    </div>
  );
}

function DashboardPage() {
  const user = useAuthStore((state) => state.user);
  const initialRange = useMemo(() => buildPresetRange('7d'), []);
  const [dateRange, setDateRange] = useState(initialRange);
  const [preset, setPreset] = useState<DateRangePreset>('7d');
  const [games, setGames] = useState<DeveloperGameAnalytics[]>([]);
  const [gameTrends, setGameTrends] = useState<DeveloperGameTrendAnalytics[]>([]);
  const [activeGameId, setActiveGameId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGames = async () => {
      setLoading(true);
      setError(null);

      try {
        const [analyticsResponse, trendResponse] = await Promise.all([
          apiClient.getDeveloperGamesAnalyticsByDateRange(dateRange.startDate, dateRange.endDate),
          apiClient.getDeveloperGameTrendAnalyticsByDateRange(dateRange.startDate, dateRange.endDate),
        ]);

        if (analyticsResponse.success) {
          setGames(analyticsResponse.data);
        }

        if (trendResponse.success) {
          setGameTrends(trendResponse.data);
        }

      } catch (fetchError) {
        console.error('Failed to fetch developer analytics:', fetchError);
        setError('Failed to load game analytics. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    void fetchGames();
  }, [dateRange.endDate, dateRange.startDate]);

  const handleRangeApply = (nextStartDate: string, nextEndDate: string) => {
    setDateRange({ startDate: nextStartDate, endDate: nextEndDate });
    setPreset(getPresetFromDateRange(nextStartDate, nextEndDate));
  };

  const handlePresetChange = (nextPreset: DateRangePreset) => {
    setPreset(nextPreset);

    if (nextPreset === 'custom') {
      return;
    }

    setDateRange(buildPresetRange(nextPreset));
  };

  useEffect(() => {
    if (games.length === 0) {
      setActiveGameId(null);
      return;
    }

    setActiveGameId((current) => current && games.some(({ game }) => game.id === current) ? current : games[0].game.id);
  }, [games]);

  const stats = {
    total: games.length,
    pending: games.filter(({ game }) => game.reviewStatus === 'pending_review').length,
    approved: games.filter(({ game }) => game.reviewStatus === 'approved').length,
    rejected: games.filter(({ game }) => game.reviewStatus === 'rejected').length,
  };

  const trendMap = useMemo(
    () => new Map(gameTrends.map((entry) => [entry.gameId, entry.trend])),
    [gameTrends]
  );

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Welcome, {user?.firstName}</h1>
          <p className="text-muted-foreground mt-1">
            {user?.isVerifiedDeveloper ? (
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Verified Developer
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-500" />
                Pending Verification - You cannot submit games until verified
              </span>
            )}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Games</CardTitle>
              <Gamepad2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{loading ? '...' : stats.total}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Review</CardTitle>
              <Clock className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{loading ? '...' : stats.pending}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Approved</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{loading ? '...' : stats.approved}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Rejected</CardTitle>
              <AlertCircle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{loading ? '...' : stats.rejected}</div>
            </CardContent>
          </Card>
        </div>

        {/* Game Analytics */}
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <CardTitle>Game Analytics</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Switch between your games to review growth, installs, player activity, and engagement trends for the selected date range.
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-muted-foreground">Loading...</p>
            ) : error ? (
              <p className="text-sm text-red-500">{error}</p>
            ) : games.length === 0 ? (
              <p className="text-muted-foreground">No games yet. Submit your first game!</p>
            ) : (
              <Tabs value={activeGameId ?? games[0].game.id} onValueChange={setActiveGameId} className="w-full">
                <div className="overflow-x-auto pb-2">
                  <TabsList className="inline-flex h-auto min-w-max justify-start gap-1 rounded-lg bg-muted p-1">
                    {games.map(({ game }) => (
                      <TabsTrigger
                        key={game.id}
                        value={game.id}
                        className="rounded-md px-4 py-2 text-sm font-semibold text-foreground/70 transition-all hover:bg-background/60 hover:text-foreground data-[state=active]:bg-[hsl(var(--color-primary-action))] data-[state=active]:text-[hsl(var(--color-primary-action-foreground))] data-[state=active]:shadow"
                      >
                        {game.title}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>

                {games.map(({ game, analytics }) => (
                  <TabsContent key={game.id} value={game.id} className="mt-4">
                    <GameAnalyticsTab
                      game={game}
                      analytics={analytics}
                      trend={trendMap.get(game.id) ?? []}
                      startDate={dateRange.startDate}
                      endDate={dateRange.endDate}
                      preset={preset}
                      onApplyRange={handleRangeApply}
                      onChangePreset={handlePresetChange}
                    />
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
}

